import { useCallback, useMemo } from 'react';
import { SpacingScale, useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { ListCell } from '@cbhq/cds-web/cells';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalHeader, ModalProps, Tooltip } from '@cbhq/cds-web/overlays';
import { ThemeProvider } from '@cbhq/cds-web/system';
import { TextBody } from '@cbhq/cds-web/typography';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { formatDate } from ':cloud/utils/date';
import Permission from ':cloud/widgets/auth';
import { OWNER_ADMIN_ROLES } from ':cloud/widgets/auth/util';
import { ProjectDeleteModal } from ':cloud/widgets/projects/ProjectDeleteModal';
import { ProjectEditModal } from ':cloud/widgets/projects/ProjectEditModal';
import { ClipboardIcon } from ':cloud/widgets/sharedcomponents';

type ProjectIdMobileProps = { id?: string };
function ProjectIdMobile({ id }: ProjectIdMobileProps) {
  return (
    <VStack gap={1}>
      <TextBody as="span" color="foregroundMuted">
        Project ID
      </TextBody>
      <HStack alignItems="center" display="inline-flex" gap={2}>
        <Box background="primaryWash" spacing={0.5}>
          <TextBody mono as="span" color="foregroundMuted">
            {id}
          </TextBody>
        </Box>
        <ClipboardIcon text={id || ''} iconSize="s" />
      </HStack>
    </VStack>
  );
}

type ProjectSettingsModalProps = Pick<ModalProps, 'visible' | 'onRequestClose'>;

export function ProjectSettingsModal({ visible, onRequestClose }: ProjectSettingsModalProps) {
  const { selectedProject } = useProject();
  const { isPhone } = useSimpleBreakpoints();

  const [isDeleteModalVisible, { toggleOn: showDeleteModal, toggleOff: hideDeleteModal }] =
    useToggler(false);
  const [isEditModalVisible, { toggleOn: showEditModal, toggleOff: hideEditModal }] =
    useToggler(false);

  const listCellSpacing = useMemo(
    () => ({
      innerSpacing: { spacingHorizontal: 3 as SpacingScale },
      outerSpacing: { spacingHorizontal: 0 as SpacingScale },
    }),
    [],
  );

  const deleteTooltip = 'Contact your admin to delete this project';
  const editTooltip = 'Contact your admin to edit this project';

  const handleOnEditPress = useCallback(() => {
    showEditModal();
    onRequestClose?.();
  }, [showEditModal, onRequestClose]);

  const handleOnDeletePress = useCallback(() => {
    showDeleteModal();
    onRequestClose?.();
  }, [showDeleteModal, onRequestClose]);

  return (
    <ThemeProvider scale="small">
      <Modal visible={visible} onRequestClose={onRequestClose}>
        <ModalHeader title="Project settings" />
        <ModalBody>
          <VStack>
            <ListCell
              description="Project name"
              detail={selectedProject?.name || ''}
              compact
              {...listCellSpacing}
            />
            {isPhone ? (
              <ProjectIdMobile id={selectedProject?.id} />
            ) : (
              <ListCell
                description="Project ID"
                subdetail={
                  <HStack alignItems="center" display="inline-flex" gap={2}>
                    <Box background="primaryWash" spacing={0.5}>
                      <TextBody mono as="span" color="foregroundMuted">
                        {selectedProject?.id}
                      </TextBody>
                    </Box>
                    <ClipboardIcon text={selectedProject?.id || ''} iconSize="s" />
                  </HStack>
                }
                compact
                {...listCellSpacing}
              />
            )}
            <ListCell
              description="Date created"
              detail={formatDate(new Date(selectedProject?.createdAt || new Date()), 'MMM d, yyyy')}
              compact
              {...listCellSpacing}
            />
          </VStack>
        </ModalBody>
        <HStack
          borderedTop
          gap={2}
          justifyContent="flex-end"
          spacingHorizontal={3}
          spacingVertical={2}
          width="100%"
        >
          <Permission
            permission={OWNER_ADMIN_ROLES}
            fallback={
              <Tooltip content={editTooltip} placement="bottom">
                <Button variant="secondary" disabled>
                  Edit
                </Button>
              </Tooltip>
            }
          >
            <Button variant="secondary" onPress={handleOnEditPress}>
              Edit
            </Button>
          </Permission>
          <Permission
            permission={OWNER_ADMIN_ROLES}
            fallback={
              <Tooltip content={deleteTooltip} placement="bottom">
                <Button variant="negative" disabled>
                  Delete
                </Button>
              </Tooltip>
            }
          >
            <Button variant="negative" onPress={handleOnDeletePress}>
              Delete
            </Button>
          </Permission>
        </HStack>
      </Modal>
      <ProjectEditModal visible={isEditModalVisible} onRequestClose={hideEditModal} />
      <ProjectDeleteModal visible={isDeleteModalVisible} onRequestClose={hideDeleteModal} />
    </ThemeProvider>
  );
}
